import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import jwt_decode from 'jwt-decode';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, throwError } from 'rxjs';

import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';

import { selectUser } from '../../../store/app.selectors';

import { EpochTime } from '../../../core/interfaces/IEpochTime';
import { skipApiKey, skipMessagingKey } from '../../../core/services/http.context';
import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { LoggerService } from '../../../core/services/logger.service';
import { setUser, unSetUser } from '../../../modules/user/store/user.actions';
import { DealerSettingName, IDealerSetting } from '../../chat/interfaces/IDealerSettings';
import { IMessageableEntity } from '../../chat/interfaces/IMessageableEntity';
import { WorkspaceFeatures } from '../../chat/interfaces/IWorkspace';
import { PermissionsService } from '../../chat/services/permissions.service';
import { unsetChat } from '../../chat/store/chat.actions';
import { AppStateWithChat } from '../../chat/store/chat.reducer';
import { setChannelsLoaded, setLoadingResolve } from '../../shared/store/ui.actions';
import { IAwaySettings, IProfile, IProfilesResponse, IUserProfile } from '../interfaces/IUserProfile';

export interface ILoginData {
  username: string;
  password: string;
  dealerId: string;
}

type DecodedToken = { accountUid: string; sub: string; displayName: string; subAccountUID: string };

@Injectable()
export class AuthenticationService {
  private store = inject(Store<AppStateWithChat>);
  private router = inject(Router);
  private env = inject(MessagingEnvironmentService);
  private permissionService = inject(PermissionsService);
  private logger = inject(LoggerService);
  public http = inject(HttpClient);

  private readonly isAuthenticatedState = signal(false);
  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  JwtHelper = new JwtHelperService();

  constructor() {
    this.checkToken();
  }

  async setTokenAndDealerId(token: string, dealerId: string) {
    localStorage.setItem('messaging-token', token);
    localStorage.setItem('dealerId', dealerId);
    this.isAuthenticatedState.set(true);
  }

  async loginWithParams(token: string) {
    localStorage.setItem('messaging-token', token);
    this.isAuthenticatedState.set(true);
  }

  logout(dealerId: string | null = null, redirect = false, isSignature = false) {
    localStorage.removeItem('messaging-token');
    localStorage.removeItem('messaging-channels');
    localStorage.removeItem('messaging-api-token');
    localStorage.removeItem('messaging-api-refresh-token');
    const theme = localStorage.getItem('data-theme');
    localStorage.clear();
    if (theme) {
      localStorage.setItem('data-theme', theme);
    }
    this.store.dispatch(unsetChat());
    this.store.dispatch(unSetUser());
    this.store.dispatch(setChannelsLoaded({ value: false }));
    this.store.dispatch(setLoadingResolve({ value: false }));
    this.isAuthenticatedState.set(false);
    if (isSignature) {
      this.router.navigateByUrl('/error-screen');
      return;
    }
    let urlLogin = '/login';
    if (dealerId) {
      urlLogin = urlLogin + `?dealerId=${dealerId}`;
    }
    if (redirect) {
      urlLogin = urlLogin + '&redirect=true';
    }
    this.router.navigateByUrl(urlLogin);
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedState();
  }

  /** @deprecated Use {@link isAuthenticated} instead */
  isAuthenticaed(): boolean {
    return this.isAuthenticated();
  }

  checkToken() {
    if (localStorage.getItem('messaging-token') || localStorage.getItem('messaging-api-token')) {
      this.isAuthenticatedState.set(true);
    }
  }

  decodeToken(token_name: string): DecodedToken | null {
    const token = localStorage.getItem(token_name);
    return token ? jwt_decode<DecodedToken>(token) : null;
  }

  setUserInfo(dealerSettings: IDealerSetting[]): void {
    const decodedToken = this.decodeToken('messaging-token');
    if (decodedToken) {
      const data: IUserProfile = {
        accountUID: '',
        authenticated: true,
        groups: [],
        provider: '',
        subAccountMessageableEntity: { displayName: '', uid: '' },
        subAccountUID: '',
        subjectMessageableEntity: {
          businessReference: {
            entityType: '',
            entityUID: decodedToken.sub,
          },
          displayName: decodedToken.displayName,
          uid: '',
        },
        businessReference: { entityType: '', entityUID: decodedToken.accountUid },
        createdAt: { _seconds: 0, _nanoseconds: 0 },
        displayName: decodedToken.displayName,
        uid: '',
        updatedAt: { _seconds: 0, _nanoseconds: 0 },
        dealerSettings,
        subAccountGroups: [],
        permissions: [],
        roles: [],
      };
      this.store.dispatch(setUser({ profileInfo: data }));
    }
  }

  epochToDate(epochTime: EpochTime): Date | null {
    if (epochTime) {
      const timeTotal = epochTime._seconds * 1000 + Math.floor(epochTime._nanoseconds / 1000000);
      return new Date(timeTotal);
    } else {
      return null;
    }
  }

  async loadProfile(data: { dealerSettings: IDealerSetting[]; subAccountUID?: string }): Promise<void> {
    if (!this.user()) {
      this.store.dispatch(setLoadingResolve({ value: true }));
      if (data.subAccountUID) {
        await this.checkSubAccountToken(data.subAccountUID, data.dealerSettings);
      } else {
        this.setUserInfo(data.dealerSettings);
        await this.checkData(data.dealerSettings);
      }
    } else {
      if (data.subAccountUID) {
        await this.checkSubAccountToken(data.subAccountUID, data.dealerSettings);
      }
    }
  }

  checkTokens(): void {
    const messagingApiToken = localStorage.getItem('messaging-api-token');
    const messagingToken = localStorage.getItem('messaging-token');
    if (messagingApiToken && messagingToken) {
      const tokenApiDecoded = jwt_decode(messagingApiToken) as {
        subAccountMessageableEntity: IMessageableEntity;
        subjectMessageableEntity: IMessageableEntity;
      };
      const tokenDecoded = jwt_decode(messagingToken) as { sub: string; accountUid: string };
      if (
        tokenApiDecoded?.subAccountMessageableEntity?.businessReference?.entityUID !== tokenDecoded?.accountUid ||
        tokenApiDecoded?.subjectMessageableEntity?.businessReference?.entityUID !== tokenDecoded?.sub
      ) {
        localStorage.removeItem('messaging-api-token');
      }
    }
  }

  async checkData(dealerSettings: IDealerSetting[]): Promise<void> {
    const user = await firstValueFrom(this.getUserData());
    this.store.dispatch(setUser({ profileInfo: this.applyDealerNameFromSettings(user, dealerSettings) }));
    this.store.dispatch(setLoadingResolve({ value: false }));
  }

  getMessagingApiToken() {
    localStorage.removeItem('messaging-api-token');
    localStorage.removeItem('messaging-api-refresh-token');
    const token = localStorage.getItem('messaging-token');
    return this.http
      .post<{ token: string; refreshToken: string }>(
        `${this.env.env.apiUrl + this.env.env.messagingApiPath}/auth/handshake`,
        {},
        {
          headers: {
            'x-authorization-truvideo': `Bearer ${token}`,
          },
          context: skipApiKey(),
        }
      )
      .pipe(
        map((res) => {
          localStorage.setItem('messaging-api-token', res.token as string);
          localStorage.setItem('messaging-api-refresh-token', res.refreshToken as string);
          return res.token;
        })
      );
  }

  refreshMessagingApiToken(refreshToken: string) {
    return this.http
      .post<{ token: string }>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/auth/refresh`, { refreshToken })
      .pipe(
        map((res) => {
          return res.token;
        })
      );
  }

  async checkSubAccountToken(subAccountUID: string, dealerSettings: IDealerSetting[]): Promise<void> {
    const decodedToken = this.decodeToken('messaging-api-token');
    if (!decodedToken || decodedToken.subAccountUID !== subAccountUID) {
      const profiles = await this.getProfiles();
      const profile = profiles.find((e) => {
        return e.subAccountUID === subAccountUID;
      });
      if (profile) {
        const newToken = await this.getNewTokenByProfileUID(profile._id);
        localStorage.setItem('messaging-token', newToken);
      } else {
        this.logger.warn('Profile not found for subAccountUID', subAccountUID);
      }
    }
    await this.checkData(dealerSettings);
  }
  async getProfiles(): Promise<IProfile[]> {
    const res = await firstValueFrom(this.http.get<IProfilesResponse>(`${this.env.env.authenticationApiURL}/profiles`));
    return res.profiles ?? [];
  }

  getNewTokenByProfileUID(profileUID: string): Promise<string> {
    return firstValueFrom(
      this.http
        .post<{ accessToken: string }>(`${this.env.env.authenticationApiURL}/auth/switch-profile/${profileUID}`, {})
        .pipe(
          map((res) => {
            return res.accessToken;
          })
        )
    );
  }

  login(data: ILoginData): Observable<{ token: string; dealerId: string }> {
    return this.http
      .post<{ token: string; dealerId: string }>(
        `${this.env.env.ordersConversationApiUrl}/${data.dealerId}/authentication/login`,
        {
          username: data.username,
          password: data.password,
          deviceUid: '1',
        },
        {
          context: skipApiKey(),
        }
      )
      .pipe(
        map((res) => {
          return { token: res.token, dealerId: data.dealerId };
        })
      );
  }

  getUserProfile(): Observable<IUserProfile> {
    return forkJoin([this.getUserData(), this.getDealerSettings()]).pipe(
      map((e) => {
        const user = this.applyDealerNameFromSettings(e[0], e[1]);
        const dealerId = user?.subAccountMessageableEntity?.businessReference?.entityUID;
        if (dealerId) {
          localStorage.setItem('dealerId', dealerId);
        }
        if (!user.subjectMessageableEntity) {
          this.logger.warn('Account without subject messageable entity');
          const decodedToken = this.decodeToken('messaging-token');
          const subjectMessageableEntity: IMessageableEntity = {
            businessReference: {
              entityType: '',
              entityUID: decodedToken?.sub ?? '',
            },
            uid: '',
            displayName: decodedToken?.displayName ?? '',
          };
          user.subjectMessageableEntity = subjectMessageableEntity;
        }
        return user;
      })
    );
  }

  /** Overwrite JWT dealer displayName with dealerSettings.dealerName when present. */
  private applyDealerNameFromSettings(user: IUserProfile, dealerSettings: IDealerSetting[]): IUserProfile {
    const dealerName = (dealerSettings || []).find((setting) => setting.key === DealerSettingName.dealerName)?.value?.trim();
    const next: IUserProfile = { ...user, dealerSettings };
    if (dealerName) {
      next.subAccountMessageableEntity = {
        ...(next.subAccountMessageableEntity ?? { uid: '', displayName: '' }),
        displayName: dealerName,
      };
    }
    return next;
  }

  getUserData(): Observable<IUserProfile> {
    this.checkTokens();
    const messagingApiToken = localStorage.getItem('messaging-api-token');
    if (messagingApiToken) {
      const tokendDecoded = jwt_decode(messagingApiToken) as IUserProfile;
      if (!tokendDecoded?.subjectMessageableEntity) {
        return of(tokendDecoded);
      }
      return this.http
        .get<IMessageableEntity>(
          `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/${
            tokendDecoded.subjectMessageableEntity.uid
          }`
        )
        .pipe(
          map((user) => {
            const response = { ...tokendDecoded, subjectMessageableEntity: user };
            response.away = this.setAway(user);
            return response;
          })
        );
    } else {
      return this.http.get<IUserProfile>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/profile/me`).pipe(
        map((user) => {
          user.away = this.setAway(user.subjectMessageableEntity);
          return user;
        })
      );
    }
  }

  setAway(user: IMessageableEntity): IAwaySettings | undefined {
    if (user?.status !== 'AWAY' || !user.settings?.awaySettings?.endDate) {
      return undefined;
    }
    const to = this.epochToDate(user.settings.awaySettings.endDate);
    if (!to) {
      return undefined;
    }
    return {
      to,
      userId: user.settings.awaySettings.delegateUID ?? '',
      message: user.settings.awaySettings.customMessage ?? '',
    };
  }

  getDealerSettings(): Observable<IDealerSetting[]> {
    // return of([]);
    if (!this.permissionService.checkWorkspaceFeatures([WorkspaceFeatures.canUseDealerSettings])) {
      return of([]);
    }
    const decodedToken = this.decodeToken('messaging-token');
    if (!decodedToken) {
      return of([]);
    }
    return this.http.get<IDealerSetting[]>(
      `${this.env.env.ordersConversationApiUrl}/registration/load-settings?account-uid=${decodedToken.accountUid}`,
      {
        context: skipMessagingKey(),
      }
    );
  }

  changeTokenByDealerID(dealerId: string) {
    return this.http.post<{ token: string }>(
      `${this.env.env.ordersConversationApiUrl}/${dealerId}/authentication/exchange`,
      {}
    );
  }

  getTokenBySignature(data: {
    signature: string;
    phoneNumber: string;
    accountUID: string;
    subAccountUID: string;
    apiKey: string;
    timestamp: string;
  }) {
    return this.http
      .post<{ token: string }>(
        `${this.env.env.apiUrl + this.env.env.messagingApiPath}/signature/validate`,
        {
          accountUID: data.accountUID,
          subAccountUID: data.subAccountUID,
          customerPhoneNumber: data.phoneNumber,
        },
        {
          headers: {
            'x-authorization-signature': data.signature,
            'x-authorization-timestamp': data.timestamp,
            'x-authorization-api-key': data.apiKey,
          },
          context: skipApiKey(),
        }
      )
      .pipe(
        map((res) => {
          localStorage.setItem('signature-phone-number', data.phoneNumber);
          return res;
        }),
        catchError((err) => {
          const phoneNumber = localStorage.getItem('signature-phone-number');
          if (phoneNumber && phoneNumber !== data.phoneNumber) {
            this.logout(null, false, true);
          }
          return throwError(() => err);
        })
      );
  }
}
